import * as React from 'react';
import { useState, useEffect } from 'react';
import { Filter, ShieldCheck, Loader2, Zap, Download, AlertTriangle, ArrowRight, Eye } from 'lucide-react';
import { motion } from 'motion/react';
import { cn } from '../../lib/utils';
import { safeToFixed } from '../../lib/formatters';
import { screenStocks } from '../../services/geminiService';
import type { ScreenerOutput, MarketCapCategory, ScreenerResult, NearMissResult } from '../../services/geminiService';
import type { ScreenerCategoryData } from '../../types/analysis';
import { auth } from '../../firebase';

export function Screener({ 
  onAnalyze, 
  results, 
  onResultsChange,
  selectedCap,
  onCapChange,
  onExport,
  isExporting,
  screenerRef,
  onLoadingChange
}: { 
  onAnalyze: (ticker: string, options?: { source: 'funnel' | 'preInflection'; logId: string | null }) => void,
  results: Record<MarketCapCategory, ScreenerCategoryData>,
  onResultsChange: (output: ScreenerOutput) => void,
  selectedCap: MarketCapCategory,
  onCapChange: (cap: MarketCapCategory) => void,
  onExport: () => void,
  isExporting: boolean,
  screenerRef: React.RefObject<HTMLDivElement>,
  onLoadingChange?: (loading: boolean) => void
}) {
  const [loading, setLoading] = useState(false);

  // Mirror internal loading to the parent so the inactivity timer can pause
  // during a screener batch run. Covers every set-site and error path.
  useEffect(() => {
    onLoadingChange?.(loading);
  }, [loading, onLoadingChange]);

  const [error, setError] = useState<string | null>(null);
  const [funnelLogIds, setFunnelLogIds] = useState<Record<string, string>>({});

  const getLastRunLabel = (lastRun: string | null): { text: string; color: string } => {
    if (!lastRun) return { text: "Never run", color: "text-ink-500" };
    const days = Math.floor((Date.now() - new Date(lastRun).getTime()) / (1000 * 60 * 60 * 24));
    if (days === 0) return { text: "Run today", color: "text-success-600" };
    if (days === 1) return { text: "Run yesterday", color: "text-success-600" };
    if (days <= 7)  return { text: `Run ${days} days ago`, color: "text-warning-600" };
    return { text: `Run ${days} days ago`, color: "text-danger-600" };
  };

  const currentCategory = results[selectedCap] || { results: [], nearMisses: [], lastRun: null };
  const currentResults = currentCategory.results;
  const nearMisses = currentCategory.nearMisses || [];
  const { text: lastRunText, color: lastRunColor } = getLastRunLabel(currentCategory.lastRun);

  const [sortBy, setSortBy] = useState<'qualityScore' | 'default'>('qualityScore');

  const sortedResults = sortBy === 'qualityScore'
    ? [...currentResults].sort((a, b) => (b.qualityScore ?? 0) - (a.qualityScore ?? 0))
    : currentResults;

  const getQualityStyle = (label?: string) => {
    switch (label) {
      case 'Elite':  return { badge: 'bg-success-50 text-success-700 border-success-100', dot: 'bg-success-600' };
      case 'High':   return { badge: 'bg-oxblood-50 text-oxblood border-oxblood/20',      dot: 'bg-oxblood' };
      case 'Solid':  return { badge: 'bg-warning-50 text-warning-700 border-warning-100', dot: 'bg-warning-600' };
      case 'Watch':  return { badge: 'bg-surface-2 text-ink-600 border-ink-200',          dot: 'bg-ink-300' };
      default:       return { badge: 'bg-surface-2 text-ink-500 border-ink-200',          dot: 'bg-ink-300' };
    }
  };

  const handleRunScreener = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await screenStocks(selectedCap);
      onResultsChange(data);
      
      const newResults = data.results;
      if (newResults && newResults.length > 0) {
        const picks = newResults.map(r => ({
          symbol: r.ticker,
          category: r.qualityLabel ?? 'UNKNOWN',
          qualityScore: r.qualityScore ?? 0,
          qualityComponents: r.qualityComponents ?? {
            profitability: 0,
            consistency: 0,
            efficiency: 0,
            balanceSheet: 0,
            valuation: 0,
          },
          fcfYield: r.metrics?.fcfYield ?? 0,
          passedCriteria: [],
          metricsSnapshot: r.metrics ?? {},
        }));
        
        const token = await auth.currentUser?.getIdToken();
        fetch('/api/funnel/log-run', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
          body: JSON.stringify({ picks, category: selectedCap, diagnostics: data.diagnostics ?? null }),
        })
          .then(res => res.json())
          .then(resData => {
            if (resData.logIds) {
              setFunnelLogIds(prev => ({ ...prev, ...resData.logIds }));
            }
          })
          .catch(err => console.error('[funnel] log-run failed:', err));
      }
    } catch (err: any) {
      console.error("Screener Error:", err);
      const errorStr = typeof err === 'string' ? err : (err instanceof Error ? err.message : JSON.stringify(err));
      const errorMsg = err?.message || err?.error?.message || "";
      const combinedMsg = `${errorStr} ${errorMsg}`.toLowerCase();
      const statusCode = err?.status || err?.error?.code || err?.code || 0;

      if (combinedMsg.includes("forbidden") || combinedMsg.includes("403") || statusCode === 403 || statusCode === "403" || combinedMsg.includes("api key not valid")) {
        setError("API Key Error: The AI service rejected the request. Please click the button below to select a valid API key to resolve this.");
      } else if (combinedMsg.includes("429") || statusCode === 429 || statusCode === "429" || combinedMsg.includes("quota")) {
        setError("AI Quota exceeded. The free tier of the Gemini API has a limit. Please try again in 1 minute.");
      } else if (combinedMsg.includes("503") || statusCode === 503 || statusCode === "503" || combinedMsg.includes("unavailable") || combinedMsg.includes("high demand")) {
        setError("The AI service is currently experiencing high demand (503). We attempted multiple retries and fallbacks, but the service is still overloaded. Please wait a few moments and try again.");
      } else {
        setError(errorStr || "Failed to run the funnel. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-12 py-6 md:py-12">
      <div className="max-w-3xl mx-auto text-center space-y-6">
        <div className="inline-flex items-center justify-center w-20 h-20 bg-oxblood rounded-lg text-paper mb-4">
          <Filter className="w-10 h-10" />
        </div>
        <h1 className="text-3xl md:text-5xl font-serif font-medium italic">The Funnel</h1>
        <p className="text-ink-500 text-lg font-serif italic">
          "The stock market is filled with individuals who know the price of everything, but the value of nothing."
          <br />
          <span className="text-[10px] font-medium uppercase tracking-widest text-ink-500 not-italic">— Philip Fisher</span>
        </p>

        <div className="pt-8 flex flex-col items-center gap-6">
          <div className="oi-card p-4 md:p-8 max-w-2xl text-left">
            <h3 className="text-[10px] font-medium uppercase tracking-[0.4em] text-ink-500 mb-6 flex items-center gap-2">
              <ShieldCheck className="w-4 h-4" />
              Screening Criteria
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
              {[
                "ROIC > 12% (Capital Efficiency)",
                "ROE > 15% (Profitability)",
                "Debt/Equity < 1 (Financial Strength)",
                "Interest Coverage > 5 (Safety)",
                "5-Yr Positive Net Income & FCF",
                "Revenue Growth > 3% (5-Yr CAGR)",
                "FCF Yield > 4% (Primary Value Anchor)",
                "Gross Margin > 40% (Pricing Power)",
                "FCF Stable or Growing (5-Yr)",
                "ROE Quality-Adjusted for Leverage"
              ].map((criterion, i) => (
                <div key={i} className="flex items-center gap-3 text-xs text-ink-600">
                  <div className="w-1.5 h-1.5 rounded-full bg-oxblood" />
                  {criterion}
                </div>
              ))}
            </div>
            <div className="mt-6 pt-6 border-t border-ink-200">
              <p className="text-[10px] text-ink-500 leading-relaxed">
                The Funnel identifies businesses with durable economics — high returns on capital, 
                consistent cash flows, and financial strength. An Elite score means the business 
                has passed a rigorous quality screen. It does <span className="font-medium text-ink-500">not</span> mean 
                the stock is a buy at today's price. Valuation, margin of safety, and the 
                final investment verdict are determined by the Oracle Analysis.
              </p>
            </div>
          </div>

          <div className="flex items-center justify-center gap-2 p-1 bg-surface-2 rounded-md border border-ink-200">
            {(['large', 'mid', 'small'] as MarketCapCategory[]).map((cap) => (
              <button
                key={cap}
                onClick={() => onCapChange(cap)}
                className={`px-3 py-1.5 md:px-6 md:py-2 rounded-md text-[10px] font-medium uppercase tracking-widest transition-all ${
                  selectedCap === cap 
                    ? 'bg-oxblood text-paper' 
                    : 'text-ink-500 hover:text-ink hover:bg-surface'
                }`}
              >
                {cap} Cap
              </button>
            ))}
          </div>

          <div className="flex flex-col md:flex-row items-center justify-center gap-3 md:gap-4 w-full px-4 md:px-0">
            <button
               onClick={handleRunScreener}
              disabled={loading}
              className="group relative w-full md:w-auto px-6 py-4 md:px-12 md:py-5 bg-oxblood text-paper rounded-md font-medium uppercase tracking-[0.2em] text-xs hover:bg-oxblood-800 transition-all disabled:opacity-50"
            >
              {loading ? (
                <div className="flex items-center gap-3">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>Pouring the {selectedCap.charAt(0).toUpperCase() + selectedCap.slice(1)} Market...</span>
                </div>
              ) : (
                <div className="flex items-center gap-3">
                  <Zap className="w-4 h-4" />
                  <span>Run {selectedCap.charAt(0).toUpperCase() + selectedCap.slice(1)} Cap Screener</span>
                </div>
              )}
            </button>

             {currentResults.length > 0 && !loading && (
              <button
                onClick={onExport}
                disabled={isExporting}
                className="w-full md:w-auto px-6 py-4 md:px-8 md:py-5 bg-surface text-ink border border-ink-200 rounded-md font-medium uppercase tracking-[0.2em] text-xs hover:bg-surface-2 transition-all flex items-center justify-center gap-3 disabled:opacity-50"
              >
                {isExporting ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Download className="w-4 h-4" />
                )}
                <span>Download Report</span>
              </button>
            )}
          </div>

          <div className="flex items-center justify-center gap-2 mt-2">
            <span className={`text-[10px] font-medium uppercase tracking-widest ${lastRunColor}`}>
              {lastRunText}
            </span>
            {currentCategory.lastRun && (
              <span className="text-[10px] text-ink-300 uppercase tracking-widest">·</span>
            )}
            {currentCategory.lastRun &&
              Math.floor((Date.now() - new Date(currentCategory.lastRun).getTime())
                / (1000 * 60 * 60 * 24)) > 7 && (
                <span className="text-[10px] text-ink-500 font-serif italic">
                  Results may be stale — consider re-running
                </span>
            )}
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-8">
          {[
            { label: "Quality", value: "ROIC > 12%, ROE > 15%" },
            { label: "Safety", value: "D/E < 1, Int. Cov > 5" },
            { label: "Cash Flow", value: "FCF Yield > 4%, 5-Yr Stable" }
          ].map((f, i) => (
            <div key={i} className="oi-card p-4 text-left">
              <div className="text-[8px] font-medium uppercase tracking-widest text-ink-500 mb-1">{f.label}</div>
              <div className="text-[10px] font-medium text-ink">{f.value}</div>
            </div>
          ))}
        </div>
      </div>

      <div ref={screenerRef} className="bg-surface p-4 md:p-8 rounded-lg">
        {loading && (
          <div className="flex flex-col items-center justify-center py-20 text-center space-y-4">
            <div className="w-12 h-12 border-4 border-ink-200 border-t-oxblood rounded-full animate-spin" />
            <p className="text-[10px] font-medium uppercase tracking-widest text-ink-500">AI is scanning 8,000+ tickers for quality...</p>
          </div>
        )}

        {error && (
          <div className={cn(
            "max-w-md mx-auto p-6 border rounded-lg text-center",
             error.includes("not listed") ? "bg-warning-50 border-warning-100" : "bg-danger-50 border-danger-100"
          )}>
            <AlertTriangle className={cn("w-8 h-8 mx-auto mb-4", error.includes("not listed") ? "text-warning-600" : "text-danger-600")} />
            <p className={cn("text-sm font-medium mb-4", error.includes("not listed") ? "text-warning-700" : "text-danger-600")}>{error}</p>
            {(error.includes("403") || error.includes("API Key Error")) && (
              <button
                onClick={async () => {
                  if (window.aistudio?.openSelectKey) {
                    await window.aistudio.openSelectKey();
                    handleRunScreener();
                  }
                }}
                className="px-6 py-3 bg-oxblood text-paper rounded-md text-[10px] font-medium uppercase tracking-widest hover:bg-oxblood-800 transition-all"
              >
                Select API Key
              </button>
            )}
          </div>
        )}

        {currentResults.length > 0 && !loading && (
          <>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
              <p className="text-[10px] font-medium uppercase tracking-widest text-ink-500">
                {currentResults.length} companies passed the screen
              </p>
              <div className="flex items-center gap-2">
                <span className="text-[10px] font-medium uppercase tracking-widest text-ink-500">Sort:</span>
                <button
                  onClick={() => setSortBy(sortBy === 'qualityScore' ? 'default' : 'qualityScore')}
                  className={`px-3 py-1.5 rounded-md text-[10px] font-medium uppercase tracking-widest border transition-all ${
                    sortBy === 'qualityScore'
                      ? 'bg-oxblood text-paper border-oxblood'
                      : 'bg-surface text-ink-500 border-ink-200 hover:border-oxblood/30'
                  }`}
                >
                  {sortBy === 'qualityScore' ? 'Quality Score ↓' : 'Default Order'}
                </button>
              </div>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {sortedResults.filter(s => s && s.ticker).map((stock, idx) => (
              <motion.div
                key={`${stock.ticker}-${idx}`}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.05 }}
                className="oi-card p-4 md:p-8 transition-all group hover:border-oxblood/30"
              >
                <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-5 md:mb-8">
                  <div className="flex items-center gap-3 md:gap-4">
                    <div className="w-11 h-11 md:w-14 md:h-14 rounded-md bg-oxblood text-paper flex items-center justify-center font-serif text-lg md:text-xl font-medium italic shrink-0">
                      {stock.ticker?.[0] || '?'}
                    </div>
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-1.5 md:gap-2">
                        <span className="text-base md:text-lg font-medium">{stock.ticker}</span>
                        <span className="px-2 py-0.5 rounded-md bg-surface-2 text-[8px] font-medium uppercase tracking-widest text-ink-500">{stock.sector || 'N/A'}</span>
                        <span className="text-sm font-mono font-medium text-ink">${safeToFixed(stock.currentPrice, 2)}</span>
                      </div>
                      <div className="text-sm text-ink-500 font-serif italic truncate">{stock.name || 'Unknown Company'}</div>
                      {stock.qualityLabel && (
                        <div className="flex items-center gap-2 mt-1">
                          <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-lg text-[9px] font-medium uppercase tracking-widest border ${getQualityStyle(stock.qualityLabel).badge}`}>
                            <span className={`w-1.5 h-1.5 rounded-full ${getQualityStyle(stock.qualityLabel).dot}`} />
                            {stock.qualityLabel} · {stock.qualityScore}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => onAnalyze(stock.ticker, { source: 'funnel', logId: funnelLogIds[stock.ticker] ?? null })}
                    className="self-start md:self-auto shrink-0 px-4 py-2.5 md:px-6 md:py-3 bg-oxblood text-paper rounded-md text-[10px] font-medium uppercase tracking-widest hover:bg-oxblood-800 transition-all flex items-center gap-2"
                  >
                    <span>Analyze</span>
                    <ArrowRight className="w-3 h-3" />
                  </button>
                </div>

                <div className="md:overflow-x-auto md:-mx-0 md:px-0">
                  <div className="grid grid-cols-4 gap-2 md:gap-4 mb-5 md:mb-8">
                    {[
                      { label: "ROIC", value: stock.metrics?.roic !== undefined ? `${safeToFixed(stock.metrics.roic, 1)}%` : 'N/A' },
                      { label: "ROE", value: stock.metrics?.roe !== undefined ? `${safeToFixed(stock.metrics.roe, 1)}%` : 'N/A' },
                      { label: "P/E", value: safeToFixed(stock.metrics?.peRatio, 1) },
                      { label: "FCF Yield", value: stock.metrics?.fcfYield !== undefined ? `${safeToFixed(stock.metrics.fcfYield, 1)}%` : 'N/A' }
                    ].map((m, i) => (
                      <div key={i} className="space-y-1">
                        <div className="text-[8px] font-medium uppercase tracking-widest text-ink-500">{m.label}</div>
                        <div className="text-sm font-mono font-medium text-ink">{m.value}</div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="p-6 bg-surface-2 rounded-md">
                  <div className="text-[8px] font-medium uppercase tracking-widest text-ink-500 mb-2">Oracle Rationale</div>
                  <p className="text-xs text-ink-600 leading-relaxed">
                    {stock.rationale || 'No rationale provided.'}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
          {nearMisses.length > 0 && (
            <div className="mt-12">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-8 h-8 rounded-md bg-oxblood-50 flex items-center justify-center">
                  <Eye className="w-4 h-4 text-oxblood" />
                </div>
                <div>
                  <h3 className="text-[10px] font-medium uppercase tracking-[0.3em] text-ink">Watchlist — Near Misses</h3>
                  <p className="text-[10px] text-ink-500 font-serif italic mt-0.5">
                    Cleared all quality gates. Watchlist for better entry price.
                  </p>
                </div>
              </div>

              <div className="grid gap-3">
                {nearMisses.map((miss, i) => (
                  <div key={i} className="p-5 tone-warning rounded-md flex flex-col md:flex-row md:items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="text-sm font-medium text-ink">
                          {miss.ticker}
                        </span>
                        <span className="text-[10px] text-ink-500 font-serif italic">
                          {miss.name}
                        </span>
                        <span className="text-[9px] font-medium uppercase tracking-widest text-ink-500 bg-surface-2 border border-ink-200 px-2 py-0.5 rounded-md">
                          {miss.sector}
                        </span>
                      </div>
                      <div className="flex flex-wrap gap-1.5 mb-3">
                        {miss.failedCriteria.map((c, j) => (
                          <span key={j} className="text-[9px] font-medium uppercase tracking-widest text-danger-600 bg-danger-50 border border-danger-100 px-2 py-0.5 rounded-md">
                            ✗ {c}
                          </span>
                        ))}
                      </div>
                      <p className="text-xs text-ink-600 font-serif italic leading-relaxed mb-2">{miss.whyNoteworthy}</p>
                      <p className="text-[10px] text-warning-700 font-medium uppercase tracking-widest">
                        Watch trigger: <span className="normal-case font-serif italic font-normal">{miss.watchTrigger}</span>
                      </p>
                    </div>
                    <button
                      onClick={() => onAnalyze(miss.ticker, { source: 'funnel', logId: funnelLogIds[miss.ticker] ?? null })}
                      className="shrink-0 px-4 py-2 bg-surface text-ink border border-ink-200 rounded-md text-[10px] font-medium uppercase tracking-widest hover:bg-oxblood hover:text-paper transition-all"
                    >
                      Analyze
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
          </>
        )}
      </div>
    </div>
  );
}
